import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Calendar, Search, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    fr: {
      hero: {
        title: 'Blog & Ressources',
        subtitle: 'Conseils, guides et actualités pour mieux gérer vos locations saisonnières'
      },
      search: 'Rechercher un article...',
      allCategories: 'Toutes les catégories',
      readMore: 'Lire la suite',
      noResults: 'Aucun article trouvé',
      newsletter: {
        title: 'Restez informé',
        subtitle: 'Recevez nos derniers articles et conseils directement dans votre boîte mail',
        placeholder: 'Votre email',
        cta: 'S\'abonner',
        success: 'Merci pour votre abonnement !'
      }
    },
    en: {
      hero: {
        title: 'Blog & Resources',
        subtitle: 'Tips, guides and news to better manage your short-term rentals'
      },
      search: 'Search for an article...',
      allCategories: 'All categories',
      readMore: 'Read more',
      noResults: 'No articles found',
      newsletter: {
        title: 'Stay informed',
        subtitle: 'Receive our latest articles and tips directly in your inbox',
        placeholder: 'Your email',
        cta: 'Subscribe',
        success: 'Thank you for subscribing!'
      }
    }
  };

  const t = content[language];

  // Fetch blog posts from API
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/blog/posts`);
        setBlogPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(blogPosts.map(post => post.category[language]))];

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title[language].toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt[language].toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category[language] === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        language: language
      });
      
      if (response.data.success) {
        setSubscribed(true);
        setEmail('');
        setTimeout(() => setSubscribed(false), 3000);
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {t.hero.title}
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
          </div>

          {/* Search and Filter */}
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-900"
              >
                <option value="all">{t.allCategories}</option>
                {categories.slice(1).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-xl text-slate-500">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card
                  key={post.id}
                  className="overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title[language]}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-blue-900 text-white">
                        {post.category[language]}
                      </Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <div className="flex items-center text-sm text-slate-500 mb-2">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(post.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-900 transition-colors">
                      {post.title[language]}
                    </h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">{post.excerpt[language]}</p>
                    <Button
                      variant="ghost"
                      className="text-blue-900 hover:text-blue-800 p-0 h-auto font-semibold"
                    >
                      {t.readMore}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-xl text-slate-500">{t.noResults}</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.newsletter.title}</h2>
          <p className="text-xl text-blue-100 mb-8">{t.newsletter.subtitle}</p>
          {subscribed ? (
            <div className="bg-green-500 text-white px-6 py-3 rounded-lg inline-block">
              {t.newsletter.success}
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="max-w-md mx-auto flex gap-4">
              <Input
                type="email"
                placeholder={t.newsletter.placeholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-white text-slate-900"
              />
              <Button
                type="submit"
                className="bg-white text-blue-900 hover:bg-slate-100"
              >
                {t.newsletter.cta}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
